import { Layout } from "@/components/Layout";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, CheckCircle2, ArrowRight, ArrowLeft, Loader2, Building2, Home } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import axios from "axios";
import { format, addDays, isBefore, startOfDay } from "date-fns";
import { logError } from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Booking time windows — replaces fixed hourly slots for a more
// human-friendly, flexible scheduling experience.
const TIME_WINDOWS = [
  { value: "morning",     label: "Morning",     range: "08:00 – 11:00" },
  { value: "mid_morning", label: "Mid-Morning", range: "11:00 – 14:00" },
  { value: "afternoon",   label: "Afternoon",   range: "14:00 – 17:00" },
];

// Subscription tiers offered (Standard + Deep only — EoT is one-off)
const SUBSCRIPTION_TIERS = [
  { value: "standard", label: "Standard Clean", desc: "Maintenance service." },
  { value: "deep",     label: "Deep Clean",     desc: "Full detail service." },
];

const SUBSCRIPTION_FREQUENCIES = [
  { value: "weekly",    label: "Weekly" },
  { value: "biweekly",  label: "Bi-Weekly" },
  { value: "monthly",   label: "Monthly" },
];

// Contract (B2B) service slugs — these route to the Quote form, not the booking stepper.
const CONTRACT_SERVICE_SLUGS = new Set([
  "office-cleaning",
  "contract-cleaning",
  "service-cleaning",
  "specialist-cleaning",
]);

const PROPERTY_SIZES = [
  { value: "small", label: "Small (studio / 1 bed)" },
  { value: "medium", label: "Medium (2–3 bed)" },
  { value: "large", label: "Large (4+ bed)" },
];

const HEARD_FROM_OPTIONS = [
  "Google",
  "Facebook / Instagram",
  "Recommendation",
  "Returning customer",
  "Local advert",
  "Other",
];

const BookingPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const preSelectedService = searchParams.get("service");
  const preSelectedTier = searchParams.get("tier");       // "standard" | "deep"
  const preSelectedFrequency = searchParams.get("frequency"); // "weekly" | "biweekly" | "monthly"
  const cancelled = searchParams.get("cancelled");

  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [catalog, setCatalog] = useState(null); // subscriptions/catalog response

  const [formData, setFormData] = useState({
    service_id: preSelectedService || "",
    service_name: "",
    customer_name: "",
    customer_email: "",
    customer_phone: "",
    address: "",
    postcode: "",
    bedrooms: 1,
    bathrooms: 1,
    property_size: "small",
    has_pets: false,
    heard_from: "",
    booking_date: null,
    booking_time: "",          // legacy field, kept for back-compat
    time_window: "",            // "morning" | "mid_morning" | "afternoon"
    special_instructions: "",
    // Subscription fields
    plan_tier: preSelectedTier || "",       // "standard" | "deep" | ""
    plan_frequency: preSelectedFrequency || "",
    addons: [],                              // [string slugs]
  });

  const [calculatedPrice, setCalculatedPrice] = useState(0);
  const [postcodeStatus, setPostcodeStatus] = useState(null); // { valid, covered, area, message }
  const [validatingPostcode, setValidatingPostcode] = useState(false);

  // Promo code state
  const [promoInput, setPromoInput] = useState("");
  const [appliedPromo, setAppliedPromo] = useState(null); // { code, discount_amount, final_price, description }
  const [promoError, setPromoError] = useState("");
  const [promoChecking, setPromoChecking] = useState(false);

  // Contract (B2B) quote-request state
  const [quoteForm, setQuoteForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    postcode: "",
    message: "",
    heard_from: "",
  });
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);
  const [quoteSubmitted, setQuoteSubmitted] = useState(false);

  const isContractService = (slug) => CONTRACT_SERVICE_SLUGS.has(slug);

  useEffect(() => {
    if (cancelled) {
      toast.error("Payment was cancelled. Please try again.");
    }
  }, [cancelled]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await axios.get(`${API}/services`);
        setServices(response.data);
        
        if (preSelectedService) {
          const service = response.data.find(s => s.slug === preSelectedService);
          if (service) {
            setFormData(prev => ({
              ...prev,
              service_id: service.slug,
              service_name: service.name
            }));
          }
        }
      } catch (error) {
        logError("Error fetching services:", error);
        toast.error("Failed to load services");
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, [preSelectedService]);

  useEffect(() => {
    // Load subscription catalog (tiers, add-ons, time windows, etc.)
    axios.get(`${API}/subscriptions/catalog`)
      .then((res) => setCatalog(res.data))
      .catch((err) => logError("catalog fetch failed", err));
  }, []);

  // Price calculation — subscription quote OR legacy one-off calc-price
  useEffect(() => {
    const calculatePrice = async () => {
      const isSub = formData.plan_tier && formData.plan_frequency;
      try {
        if (isSub) {
          const res = await axios.post(`${API}/subscriptions/quote`, {
            tier: formData.plan_tier,
            bedrooms: formData.bedrooms,
            frequency: formData.plan_frequency,
            addons: formData.addons,
          });
          setCalculatedPrice(res.data.total_per_visit);
        } else if (formData.service_id && formData.bedrooms && formData.bathrooms) {
          const response = await axios.post(`${API}/calculate-price`, null, {
            params: {
              service_slug: formData.service_id,
              bedrooms: formData.bedrooms,
              bathrooms: formData.bathrooms,
            },
          });
          // Add-ons for one-off: locally derive from catalog
          let extras = 0;
          if (catalog && formData.addons.length) {
            for (const slug of formData.addons) {
              const a = catalog.addons[slug];
              if (!a) continue;
              if (a.price_pct) extras += (response.data.total_price * a.price_pct) / 100;
              else extras += Number(a.price) || 0;
            }
          }
          setCalculatedPrice(Math.round((response.data.total_price + extras) * 100) / 100);
        }
      } catch (error) {
        logError("Error calculating price:", error);
      }
    };
    calculatePrice();
  }, [
    formData.service_id,
    formData.bedrooms,
    formData.bathrooms,
    formData.plan_tier,
    formData.plan_frequency,
    formData.addons,
    catalog,
  ]);

  // If service / rooms / tier change, invalidate any applied promo so it gets re-checked
  useEffect(() => {
    setAppliedPromo(null);
    setPromoError("");
  }, [
    formData.service_id,
    formData.bedrooms,
    formData.bathrooms,
    formData.plan_tier,
    formData.plan_frequency,
  ]);

  // Auto-pre-fill promo input from URL query (?code=WELCOME20)
  useEffect(() => {
    const code = searchParams.get("code");
    if (code) setPromoInput(code.toUpperCase());
  }, [searchParams]);

  const applyPromo = async () => {
    const code = (promoInput || "").trim().toUpperCase();
    if (!code) {
      setPromoError("Enter a promo code first.");
      return;
    }
    if (!formData.service_id) {
      setPromoError("Pick a service first.");
      return;
    }
    setPromoChecking(true);
    setPromoError("");
    try {
      const res = await axios.post(`${API}/promo-codes/validate`, {
        code,
        service_slug: formData.service_id,
        bedrooms: formData.bedrooms,
        bathrooms: formData.bathrooms,
        customer_email: formData.customer_email || "",
      });
      if (res.data.valid) {
        setAppliedPromo(res.data);
        setPromoError("");
        toast.success(res.data.message);
      } else {
        setAppliedPromo(null);
        setPromoError(res.data.message || "Code not valid.");
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't validate this code.";
      setAppliedPromo(null);
      setPromoError(msg);
    } finally {
      setPromoChecking(false);
    }
  };

  const removePromo = () => {
    setAppliedPromo(null);
    setPromoInput("");
    setPromoError("");
  };

  const finalTotal = appliedPromo?.valid ? appliedPromo.final_price : calculatedPrice;

  const handleServiceSelect = (serviceId) => {
    const service = services.find(s => s.slug === serviceId);
    setFormData(prev => ({
      ...prev,
      service_id: serviceId,
      service_name: service?.name || ""
    }));
  };

  const handleDateSelect = (date) => {
    setFormData(prev => ({ ...prev, booking_date: date }));
  };

  const handleTimeSelect = (windowValue) => {
    // store both for back-compat (legacy booking_time field) and the new time_window
    setFormData((prev) => ({
      ...prev,
      time_window: windowValue,
      booking_time: windowValue,
    }));
  };

  const toggleAddon = (slug) => {
    setFormData((prev) => ({
      ...prev,
      addons: prev.addons.includes(slug)
        ? prev.addons.filter((s) => s !== slug)
        : [...prev.addons, slug],
    }));
  };

  const validateStep = (currentStep) => {
    switch (currentStep) {
      case 1:
        // Subscription mode requires tier + frequency; one-off requires service_id
        if (formData.plan_tier) {
          return Boolean(formData.plan_tier && formData.plan_frequency);
        }
        return formData.service_id !== "";
      case 2:
        return formData.booking_date && formData.time_window;
      case 3:
        return (
          formData.customer_name &&
          formData.customer_email &&
          formData.customer_phone &&
          formData.address &&
          formData.postcode &&
          postcodeStatus?.valid && postcodeStatus?.covered
        );
      default:
        return true;
    }
  };

  // Verify postcode via postcodes.io (UK government postcode lookup, no API key needed)
  const validatePostcode = async (raw) => {
    const pc = (raw || "").trim();
    if (!pc) {
      setPostcodeStatus(null);
      return;
    }
    setValidatingPostcode(true);
    try {
      const res = await axios.get(`${API}/validate-postcode`, { params: { postcode: pc } });
      setPostcodeStatus(res.data);
      if (res.data?.valid && res.data?.normalized) {
        setFormData((prev) => ({ ...prev, postcode: res.data.normalized }));
      }
    } catch (e) {
      setPostcodeStatus({ valid: false, message: "Postcode lookup failed. Please try again." });
    } finally {
      setValidatingPostcode(false);
    }
  };

  // Submit contract clean quote request
  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    if (
      !quoteForm.name.trim() ||
      !quoteForm.company.trim() ||
      !quoteForm.email.trim() ||
      !quoteForm.phone.trim()
    ) {
      toast.error("Name, company, email and phone are all required.");
      return;
    }
    setQuoteSubmitting(true);
    try {
      await axios.post(`${API}/quote-requests`, {
        ...quoteForm,
        service_slug: formData.service_id,
        service_name: selectedService?.name,
      });
      setQuoteSubmitted(true);
      toast.success("Quote request received — we'll be in touch within 1 business day.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit quote request. Please try again.");
    } finally {
      setQuoteSubmitting(false);
    }
  };

  const handleNext = () => {
    if (validateStep(step)) {
      setStep(step + 1);
    } else {
      toast.error("Please fill in all required fields");
    }
  };

  const handleBack = () => {
    setStep(step - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep(3)) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSubmitting(true);
    try {
      // Create booking
      const bookingData = {
        ...formData,
        booking_date: format(formData.booking_date, "yyyy-MM-dd"),
        total_price: finalTotal,
        promo_code: appliedPromo?.valid ? appliedPromo.code : null,
      };

      const bookingResponse = await axios.post(`${API}/bookings`, bookingData);
      const booking = bookingResponse.data;

      // Create checkout session
      const checkoutResponse = await axios.post(`${API}/checkout`, {
        booking_id: booking.id,
        origin_url: window.location.origin
      });

      // Redirect to Stripe
      window.location.href = checkoutResponse.data.url;
    } catch (error) {
      logError("Error creating booking:", error);
      toast.error("Failed to create booking. Please try again.");
      setSubmitting(false);
    }
  };

  const selectedService = services.find(s => s.slug === formData.service_id);
  const showQuoteFlow = isContractService(formData.service_id);

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  // ===== Contract (B2B) services — Quote request flow =====
  if (showQuoteFlow) {
    return (
      <Layout>
        <section className="py-24 md:py-32 bg-[#F9FAF9] min-h-screen">
          <div className="container-clean">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center max-w-2xl mx-auto mb-12"
            >
              <span className="inline-flex items-center gap-2 bg-[#E8F5E9] text-[#2D5016] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Building2 className="w-4 h-4" />
                Contract Clean — {selectedService?.name}
              </span>
              <h1
                className="text-4xl md:text-5xl font-bold text-[#1A2E12] tracking-tight mb-4"
                data-testid="quote-title"
              >
                Get a FREE Quote
              </h1>
              <p className="text-lg text-[#5C6B57]">
                Tell us about your site and we&apos;ll send a tailored quote within 1 business day.
              </p>
            </motion.div>

            <div className="max-w-2xl mx-auto">
              <Card className="bg-white border-[#E2E8E0] rounded-2xl shadow-natural">
                <CardHeader>
                  <CardTitle className="text-[#1A2E12]">
                    {quoteSubmitted ? "Thanks — we&apos;ll be in touch" : "Your details"}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {quoteSubmitted ? (
                    <div className="text-center py-8" data-testid="quote-success">
                      <CheckCircle2 className="w-14 h-14 text-[#7CB342] mx-auto mb-4" />
                      <p className="text-[#1A2E12] font-semibold mb-2">
                        Quote request received.
                      </p>
                      <p className="text-[#5C6B57] mb-6">
                        Our team will email you a tailored quote within 1 business day.
                      </p>
                      <Button
                        className="bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full"
                        onClick={() => navigate("/")}
                      >
                        Back to home
                      </Button>
                    </div>
                  ) : (
                    <form
                      onSubmit={handleQuoteSubmit}
                      className="space-y-4"
                      data-testid="quote-form"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm text-[#5C6B57]">Name *</Label>
                          <Input
                            required
                            value={quoteForm.name}
                            onChange={(e) =>
                              setQuoteForm((p) => ({ ...p, name: e.target.value }))
                            }
                            className="mt-1"
                            data-testid="quote-name"
                          />
                        </div>
                        <div>
                          <Label className="text-sm text-[#5C6B57]">Company *</Label>
                          <Input
                            required
                            value={quoteForm.company}
                            onChange={(e) =>
                              setQuoteForm((p) => ({ ...p, company: e.target.value }))
                            }
                            className="mt-1"
                            data-testid="quote-company"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm text-[#5C6B57]">Email *</Label>
                          <Input
                            required
                            type="email"
                            value={quoteForm.email}
                            onChange={(e) =>
                              setQuoteForm((p) => ({ ...p, email: e.target.value }))
                            }
                            className="mt-1"
                            data-testid="quote-email"
                          />
                        </div>
                        <div>
                          <Label className="text-sm text-[#5C6B57]">Telephone *</Label>
                          <Input
                            required
                            type="tel"
                            value={quoteForm.phone}
                            onChange={(e) =>
                              setQuoteForm((p) => ({ ...p, phone: e.target.value }))
                            }
                            className="mt-1"
                            data-testid="quote-phone"
                          />
                        </div>
                      </div>
                      <div>
                        <Label className="text-sm text-[#5C6B57]">
                          Site postcode (optional)
                        </Label>
                        <Input
                          value={quoteForm.postcode}
                          onChange={(e) =>
                            setQuoteForm((p) => ({
                              ...p,
                              postcode: e.target.value.toUpperCase(),
                            }))
                          }
                          className="mt-1"
                          placeholder="e.g. SW1A 1AA"
                          data-testid="quote-postcode"
                        />
                      </div>
                      <div>
                        <Label className="text-sm text-[#5C6B57]">
                          How did you hear about us?
                        </Label>
                        <Select
                          value={quoteForm.heard_from}
                          onValueChange={(v) =>
                            setQuoteForm((p) => ({ ...p, heard_from: v }))
                          }
                        >
                          <SelectTrigger className="mt-1" data-testid="quote-heard">
                            <SelectValue placeholder="Select an option" />
                          </SelectTrigger>
                          <SelectContent>
                            {HEARD_FROM_OPTIONS.map((opt) => (
                              <SelectItem key={opt} value={opt}>
                                {opt}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-sm text-[#5C6B57]">
                          Tell us about your site (optional)
                        </Label>
                        <Textarea
                          value={quoteForm.message}
                          onChange={(e) =>
                            setQuoteForm((p) => ({ ...p, message: e.target.value }))
                          }
                          className="mt-1"
                          rows={4}
                          placeholder="Site size, current cleaning frequency, key challenges..."
                          data-testid="quote-message"
                        />
                      </div>
                      <Button
                        type="submit"
                        disabled={quoteSubmitting}
                        className="w-full bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full py-6 text-lg"
                        data-testid="quote-submit"
                      >
                        {quoteSubmitting ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            Request my FREE quote
                            <ArrowRight className="ml-2 w-5 h-5" />
                          </>
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>

              {/* Service-switcher: let user change their mind */}
              <div className="text-center mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setFormData((p) => ({ ...p, service_id: "", service_name: "" }));
                    navigate("/booking");
                  }}
                  className="text-sm text-[#5C6B57] hover:text-[#2D5016] underline"
                  data-testid="quote-change-service"
                >
                  ← Choose a different service
                </button>
              </div>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="py-24 md:py-32 bg-[#F9FAF9] min-h-screen">
        <div className="container-clean">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-2xl mx-auto mb-12"
          >
            <h1 className="text-4xl md:text-5xl font-bold text-[#1A2E12] tracking-tight mb-4" data-testid="booking-title">
              Book Your Clean
            </h1>
            <p className="text-lg text-[#5C6B57]">
              Schedule your eco-friendly cleaning service in just a few steps
            </p>
          </motion.div>

          {/* Progress Steps */}
          <div className="max-w-3xl mx-auto mb-12">
            <div className="flex items-center justify-between">
              {["Service", "Date & Time", "Details", "Confirm"].map((label, index) => {
                const stepNumber = index + 1;
                let stepCircleClass;
                if (step > stepNumber) {
                  stepCircleClass = "bg-[#7CB342] text-white";
                } else if (step === stepNumber) {
                  stepCircleClass = "bg-[#2D5016] text-white";
                } else {
                  stepCircleClass = "bg-[#E2E8E0] text-[#5C6B57]";
                }
                return (
                  <div key={label} className="flex items-center">
                    <div className={`flex items-center justify-center w-10 h-10 rounded-full font-semibold ${stepCircleClass}`}>
                      {step > stepNumber ? <CheckCircle2 className="w-5 h-5" /> : stepNumber}
                    </div>
                    <span className={`ml-2 hidden sm:block ${
                      step === stepNumber ? "text-[#1A2E12] font-semibold" : "text-[#5C6B57]"
                    }`}>
                      {label}
                    </span>
                    {index < 3 && (
                      <div className={`w-8 sm:w-16 h-0.5 mx-2 ${
                        step > stepNumber ? "bg-[#7CB342]" : "bg-[#E2E8E0]"
                      }`} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Form Steps */}
          <div className="max-w-3xl mx-auto">
            <Card className="bg-white border-[#E2E8E0] rounded-2xl shadow-natural">
              <CardContent className="p-8">
                {/* Step 1: Service Selection */}
                {step === 1 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h2 className="text-2xl font-semibold text-[#1A2E12] mb-2">Choose Your Service</h2>
                    <p className="text-sm text-[#5C6B57] mb-6">
                      Subscribe for the best value (Standard or Deep Clean, Weekly / Bi-Weekly / Monthly), or pick a one-off service below.
                    </p>

                    {/* Subscription tier picker */}
                    <div className="mb-6 bg-[#F9FAF9] rounded-xl p-4 border border-[#E2E8E0]" data-testid="subscription-picker">
                      <div className="flex items-center justify-between mb-3">
                        <Label className="text-sm font-semibold text-[#2D5016]">
                          Subscription (best value)
                        </Label>
                        {formData.plan_tier && (
                          <button
                            type="button"
                            onClick={() =>
                              setFormData((p) => ({
                                ...p,
                                plan_tier: "",
                                plan_frequency: "",
                              }))
                            }
                            className="text-xs text-[#5C6B57] hover:text-red-600 underline"
                            data-testid="clear-subscription"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                        {SUBSCRIPTION_TIERS.map((t) => (
                          <button
                            key={t.value}
                            type="button"
                            onClick={() =>
                              setFormData((p) => ({
                                ...p,
                                plan_tier: t.value,
                                service_id: "",
                                service_name: "",
                              }))
                            }
                            className={`p-3 rounded-lg border-2 text-left transition-all ${
                              formData.plan_tier === t.value
                                ? "border-[#2D5016] bg-[#E8F5E9]"
                                : "border-[#E2E8E0] hover:border-[#7CB342] bg-white"
                            }`}
                            data-testid={`tier-${t.value}`}
                          >
                            <p className="font-semibold text-[#1A2E12]">{t.label}</p>
                            <p className="text-xs text-[#5C6B57] mt-1">{t.desc}</p>
                          </button>
                        ))}
                      </div>
                      {formData.plan_tier && (
                        <div className="grid grid-cols-3 gap-2" data-testid="frequency-picker">
                          {SUBSCRIPTION_FREQUENCIES.map((f) => (
                            <button
                              key={f.value}
                              type="button"
                              onClick={() =>
                                setFormData((p) => ({ ...p, plan_frequency: f.value }))
                              }
                              className={`py-2 px-3 rounded-lg border-2 text-sm font-medium transition-all ${
                                formData.plan_frequency === f.value
                                  ? "border-[#2D5016] bg-[#2D5016] text-white"
                                  : "border-[#E2E8E0] bg-white text-[#5C6B57] hover:border-[#7CB342]"
                              }`}
                              data-testid={`frequency-${f.value}`}
                            >
                              {f.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    <div className="relative my-6">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-[#E2E8E0]" />
                      </div>
                      <div className="relative flex justify-center text-xs">
                        <span className="bg-white px-3 text-[#5C6B57] uppercase tracking-wider">
                          or — one-off
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                      {services.map((service) => (
                        <div
                          key={service.slug}
                          onClick={() => {
                            handleServiceSelect(service.slug);
                            setFormData((p) => ({
                              ...p,
                              plan_tier: "",
                              plan_frequency: "",
                            }));
                          }}
                          className={`cursor-pointer rounded-xl border-2 p-4 transition-all ${
                            formData.service_id === service.slug && !formData.plan_tier
                              ? "border-[#2D5016] bg-[#E8F5E9]"
                              : "border-[#E2E8E0] hover:border-[#7CB342]"
                          }`}
                          data-testid={`select-service-${service.slug}`}
                        >
                          <div className="flex items-start gap-4">
                            <img 
                              src={service.image_url} 
                              alt={service.name}
                              className="w-20 h-20 rounded-lg object-cover"
                            />
                            <div className="flex-1">
                              <h3 className="font-semibold text-[#1A2E12]">{service.name}</h3>
                              <p className="text-sm text-[#5C6B57] mt-1">From £{service.base_price}</p>
                              <p className="text-xs text-[#7CB342] mt-1">{service.duration_estimate}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>

                    {(formData.service_id || formData.plan_tier) && (
                      <div className="bg-[#F9FAF9] rounded-xl p-4 mb-6">
                        <Label className="text-sm font-semibold text-[#2D5016] mb-4 block">
                          Property Size
                        </Label>
                        <div className="grid grid-cols-2 gap-4 mb-4">
                          <div>
                            <Label className="text-sm text-[#5C6B57]">Bedrooms</Label>
                            <Select 
                              value={String(formData.bedrooms)} 
                              onValueChange={(v) => setFormData(prev => ({ ...prev, bedrooms: parseInt(v) }))}
                            >
                              <SelectTrigger className="mt-1" data-testid="select-bedrooms">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {[1,2,3,4,5,6].map(n => (
                                  <SelectItem key={n} value={String(n)}>{n} bedroom{n > 1 ? 's' : ''}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <Label className="text-sm text-[#5C6B57]">Bathrooms</Label>
                            <Select 
                              value={String(formData.bathrooms)} 
                              onValueChange={(v) => setFormData(prev => ({ ...prev, bathrooms: parseInt(v) }))}
                            >
                              <SelectTrigger className="mt-1" data-testid="select-bathrooms">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {[1,2,3,4,5].map(n => (
                                  <SelectItem key={n} value={String(n)}>{n} bathroom{n > 1 ? 's' : ''}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="mb-4">
                          <Label className="text-sm text-[#5C6B57]">Size of property</Label>
                          <Select
                            value={formData.property_size}
                            onValueChange={(v) => setFormData(prev => ({ ...prev, property_size: v }))}
                          >
                            <SelectTrigger className="mt-1" data-testid="select-property-size">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {PROPERTY_SIZES.map((s) => (
                                <SelectItem key={s.value} value={s.value}>
                                  {s.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-[#E2E8E0]">
                          <div>
                            <p className="text-sm font-medium text-[#1A2E12]">
                              Do you have pets?
                            </p>
                            <p className="text-xs text-[#5C6B57]">
                              Helps our team prepare for your home.
                            </p>
                          </div>
                          <Switch
                            checked={formData.has_pets}
                            onCheckedChange={(v) =>
                              setFormData((p) => ({ ...p, has_pets: v }))
                            }
                            data-testid="switch-has-pets"
                          />
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}

                {/* Step 2: Date & Time */}
                {step === 2 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h2 className="text-2xl font-semibold text-[#1A2E12] mb-6">Select Date & Time</h2>
                    
                    <div className="mb-8">
                      <Label className="text-sm font-semibold text-[#2D5016] mb-3 block">
                        Choose a Date
                      </Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={`w-full justify-start text-left font-normal h-12 ${
                              !formData.booking_date && "text-muted-foreground"
                            }`}
                            data-testid="date-picker-trigger"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {formData.booking_date ? format(formData.booking_date, "PPP") : "Pick a date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0 booking-calendar" align="start">
                          <Calendar
                            mode="single"
                            selected={formData.booking_date}
                            onSelect={handleDateSelect}
                            disabled={(date) => isBefore(date, startOfDay(new Date())) || isBefore(date, addDays(new Date(), 1))}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>

                    <div className="mb-8">
                      <Label className="text-sm font-semibold text-[#2D5016] mb-3 block">
                        Choose a Time Window
                      </Label>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="time-window-grid">
                        {TIME_WINDOWS.map((w) => (
                          <button
                            key={w.value}
                            type="button"
                            onClick={() => handleTimeSelect(w.value)}
                            className={`p-4 rounded-xl border-2 text-left transition-all ${
                              formData.time_window === w.value
                                ? "border-[#2D5016] bg-[#E8F5E9]"
                                : "border-[#E2E8E0] hover:border-[#7CB342]"
                            }`}
                            data-testid={`time-window-${w.value}`}
                          >
                            <p className="font-semibold text-[#1A2E12]">{w.label}</p>
                            <p className="text-sm text-[#5C6B57] mt-1">{w.range}</p>
                          </button>
                        ))}
                      </div>
                      <p className="text-xs text-[#5C6B57] mt-3">
                        Your cleaner will arrive within the window. We will confirm a more specific arrival time 24 hours before your visit.
                      </p>
                    </div>

                    {/* Optional add-ons */}
                    {catalog?.addons && (
                      <div className="mb-2" data-testid="addons-section">
                        <Label className="text-sm font-semibold text-[#2D5016] mb-3 block">
                          Optional Add-ons
                        </Label>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {Object.entries(catalog.addons).map(([slug, a]) => {
                            const selected = formData.addons.includes(slug);
                            const priceLabel = a.price_pct
                              ? `+${a.price_pct}%`
                              : `+£${a.price}`;
                            return (
                              <button
                                key={slug}
                                type="button"
                                onClick={() => toggleAddon(slug)}
                                className={`flex items-start justify-between gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                                  selected
                                    ? "border-[#2D5016] bg-[#E8F5E9]"
                                    : "border-[#E2E8E0] hover:border-[#7CB342]"
                                }`}
                                data-testid={`addon-${slug}`}
                              >
                                <div className="flex-1 min-w-0">
                                  <p className="font-semibold text-[#1A2E12]">{a.name}</p>
                                  <p className="text-xs text-[#5C6B57] mt-1">{a.description}</p>
                                </div>
                                <span className="text-sm font-bold text-[#2D5016] whitespace-nowrap">
                                  {priceLabel}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </motion.div>
                )}

                {/* Step 3: Customer Details */}
                {step === 3 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h2 className="text-2xl font-semibold text-[#1A2E12] mb-6">Your Details</h2>
                    
                    <div className="space-y-4">
                      <div>
                        <Label className="text-sm font-semibold text-[#2D5016]">Full Name *</Label>
                        <Input
                          placeholder="John Smith"
                          value={formData.customer_name}
                          onChange={(e) => setFormData(prev => ({ ...prev, customer_name: e.target.value }))}
                          className="mt-1 h-12"
                          data-testid="input-name"
                        />
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-sm font-semibold text-[#2D5016]">Email *</Label>
                          <Input
                            type="email"
                            placeholder="john@example.com"
                            value={formData.customer_email}
                            onChange={(e) => setFormData(prev => ({ ...prev, customer_email: e.target.value }))}
                            className="mt-1 h-12"
                            data-testid="input-email"
                          />
                        </div>
                        <div>
                          <Label className="text-sm font-semibold text-[#2D5016]">Phone *</Label>
                          <Input
                            type="tel"
                            placeholder="+44 7700 900000"
                            value={formData.customer_phone}
                            onChange={(e) => setFormData(prev => ({ ...prev, customer_phone: e.target.value }))}
                            className="mt-1 h-12"
                            data-testid="input-phone"
                          />
                        </div>
                      </div>

                      <div>
                        <Label className="text-sm font-semibold text-[#2D5016]">Address *</Label>
                        <Input
                          placeholder="123 Main Street, London"
                          value={formData.address}
                          onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                          className="mt-1 h-12"
                          data-testid="input-address"
                        />
                      </div>

                      <div>
                        <Label className="text-sm font-semibold text-[#2D5016]">Postcode *</Label>
                        <div className="flex gap-2 mt-1">
                          <Input
                            placeholder="SW1A 1AA"
                            value={formData.postcode}
                            onChange={(e) =>
                              setFormData((prev) => ({
                                ...prev,
                                postcode: e.target.value.toUpperCase(),
                              }))
                            }
                            onBlur={(e) => validatePostcode(e.target.value)}
                            className="h-12 flex-1"
                            data-testid="input-postcode"
                          />
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() => validatePostcode(formData.postcode)}
                            disabled={validatingPostcode || !formData.postcode}
                            className="h-12 border-[#2D5016] text-[#2D5016]"
                            data-testid="verify-postcode"
                          >
                            {validatingPostcode ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              "Verify"
                            )}
                          </Button>
                        </div>
                        {postcodeStatus && (
                          <p
                            className={`text-xs mt-2 ${
                              postcodeStatus.valid && postcodeStatus.covered
                                ? "text-[#2D5016]"
                                : "text-red-600"
                            }`}
                            data-testid="postcode-status"
                          >
                            {postcodeStatus.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <Label className="text-sm font-semibold text-[#2D5016]">
                          How did you hear about us?
                        </Label>
                        <Select
                          value={formData.heard_from}
                          onValueChange={(v) =>
                            setFormData((prev) => ({ ...prev, heard_from: v }))
                          }
                        >
                          <SelectTrigger
                            className="mt-1 h-12"
                            data-testid="input-heard-from"
                          >
                            <SelectValue placeholder="Select an option" />
                          </SelectTrigger>
                          <SelectContent>
                            {HEARD_FROM_OPTIONS.map((opt) => (
                              <SelectItem key={opt} value={opt}>
                                {opt}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label className="text-sm font-semibold text-[#2D5016]">Special Instructions</Label>
                        <Textarea
                          placeholder="Any special requests or access instructions..."
                          value={formData.special_instructions}
                          onChange={(e) => setFormData(prev => ({ ...prev, special_instructions: e.target.value }))}
                          className="mt-1"
                          rows={3}
                          data-testid="input-instructions"
                        />
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Step 4: Confirmation */}
                {step === 4 && (
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h2 className="text-2xl font-semibold text-[#1A2E12] mb-6">Confirm Your Booking</h2>
                    
                    <div className="space-y-6">
                      <div className="bg-[#F9FAF9] rounded-xl p-6">
                        <h3 className="font-semibold text-[#1A2E12] mb-4">Service Details</h3>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-[#5C6B57]">Service:</span>
                            <p className="font-medium text-[#1A2E12]">
                              {formData.plan_tier
                                ? `${SUBSCRIPTION_TIERS.find((t) => t.value === formData.plan_tier)?.label} — ${SUBSCRIPTION_FREQUENCIES.find((f) => f.value === formData.plan_frequency)?.label}`
                                : selectedService?.name}
                            </p>
                          </div>
                          <div>
                            <span className="text-[#5C6B57]">Property:</span>
                            <p className="font-medium text-[#1A2E12]">{formData.bedrooms} bed, {formData.bathrooms} bath</p>
                          </div>
                          <div>
                            <span className="text-[#5C6B57]">Date:</span>
                            <p className="font-medium text-[#1A2E12]">
                              {formData.booking_date && format(formData.booking_date, "PPP")}
                            </p>
                          </div>
                          <div>
                            <span className="text-[#5C6B57]">Time window:</span>
                            <p className="font-medium text-[#1A2E12]">
                              {TIME_WINDOWS.find((w) => w.value === formData.time_window)?.label || "—"}
                              {formData.time_window && (
                                <span className="text-xs text-[#5C6B57] ml-2">
                                  ({TIME_WINDOWS.find((w) => w.value === formData.time_window)?.range})
                                </span>
                              )}
                            </p>
                          </div>
                          {formData.addons.length > 0 && (
                            <div className="col-span-2">
                              <span className="text-[#5C6B57]">Add-ons:</span>
                              <p className="font-medium text-[#1A2E12]">
                                {formData.addons
                                  .map((s) => catalog?.addons?.[s]?.name || s)
                                  .join(", ")}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="bg-[#F9FAF9] rounded-xl p-6">
                        <h3 className="font-semibold text-[#1A2E12] mb-4">Contact Details</h3>
                        <div className="space-y-2 text-sm">
                          <p><span className="text-[#5C6B57]">Name:</span> <span className="text-[#1A2E12]">{formData.customer_name}</span></p>
                          <p><span className="text-[#5C6B57]">Email:</span> <span className="text-[#1A2E12]">{formData.customer_email}</span></p>
                          <p><span className="text-[#5C6B57]">Phone:</span> <span className="text-[#1A2E12]">{formData.customer_phone}</span></p>
                          <p><span className="text-[#5C6B57]">Address:</span> <span className="text-[#1A2E12]">{formData.address}, {formData.postcode}</span></p>
                        </div>
                      </div>

                      <div className="bg-[#F9FAF9] rounded-xl p-6 border border-[#E2E8E0]" data-testid="promo-code-section">
                        <Label className="text-sm font-semibold text-[#2D5016] mb-2 block">
                          Have a promo code?
                        </Label>
                        {appliedPromo?.valid ? (
                          <div
                            className="flex items-center justify-between bg-[#E8F5E9] border border-[#7CB342] rounded-lg px-4 py-3"
                            data-testid="promo-applied"
                          >
                            <div>
                              <p className="font-mono font-semibold text-[#2D5016]">
                                {appliedPromo.code}
                              </p>
                              <p className="text-xs text-[#5C6B57]">
                                {appliedPromo.description} · You save £
                                {appliedPromo.discount_amount.toFixed(2)}
                              </p>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              onClick={removePromo}
                              className="text-[#5C6B57] hover:text-red-600"
                              data-testid="btn-remove-promo"
                            >
                              Remove
                            </Button>
                          </div>
                        ) : (
                          <>
                            <div className="flex gap-2">
                              <Input
                                placeholder="e.g. WELCOME20"
                                value={promoInput}
                                onChange={(e) =>
                                  setPromoInput(e.target.value.toUpperCase())
                                }
                                className="h-12 flex-1 font-mono"
                                data-testid="input-promo-code"
                              />
                              <Button
                                type="button"
                                onClick={applyPromo}
                                disabled={promoChecking || !promoInput}
                                className="h-12 bg-[#7CB342] hover:bg-[#5A8E2A] text-white rounded-md px-6"
                                data-testid="btn-apply-promo"
                              >
                                {promoChecking ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  "Apply"
                                )}
                              </Button>
                            </div>
                            {promoError && (
                              <p
                                className="text-xs text-red-600 mt-2"
                                data-testid="promo-error"
                              >
                                {promoError}
                              </p>
                            )}
                          </>
                        )}
                      </div>

                      <div className="bg-[#2D5016] text-white rounded-xl p-6" data-testid="price-summary">
                        <div className="flex justify-between items-center text-sm text-white/80">
                          <span>Subtotal</span>
                          <span data-testid="subtotal-price">£{calculatedPrice.toFixed(2)}</span>
                        </div>
                        {appliedPromo?.valid && (
                          <div
                            className="flex justify-between items-center text-sm text-[#7CB342] mt-1"
                            data-testid="discount-line"
                          >
                            <span>Discount ({appliedPromo.code})</span>
                            <span>−£{appliedPromo.discount_amount.toFixed(2)}</span>
                          </div>
                        )}
                        <div className="h-px bg-white/20 my-3" />
                        <div className="flex justify-between items-center">
                          <span className="text-lg">Total</span>
                          <span className="text-3xl font-bold price-tag" data-testid="total-price">
                            £{finalTotal.toFixed(2)}
                          </span>
                        </div>
                        <p className="text-white/70 text-sm mt-2">
                          You will be redirected to secure payment
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Navigation Buttons */}
                <div className="flex justify-between mt-8 pt-6 border-t border-[#E2E8E0]">
                  {step > 1 ? (
                    <Button
                      variant="outline"
                      onClick={handleBack}
                      className="border-[#E2E8E0] text-[#5C6B57] rounded-full px-6"
                      data-testid="btn-back"
                    >
                      <ArrowLeft className="mr-2 w-4 h-4" />
                      Back
                    </Button>
                  ) : (
                    <div />
                  )}

                  {step < 4 ? (
                    <Button
                      onClick={handleNext}
                      className="bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full px-6 btn-primary"
                      disabled={!validateStep(step)}
                      data-testid="btn-next"
                    >
                      Next
                      <ArrowRight className="ml-2 w-4 h-4" />
                    </Button>
                  ) : (
                    <Button
                      onClick={handleSubmit}
                      className="bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full px-8 btn-primary"
                      disabled={submitting}
                      data-testid="btn-pay"
                    >
                      {submitting ? (
                        <>
                          <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        <>
                          Pay £{finalTotal.toFixed(2)}
                          <ArrowRight className="ml-2 w-4 h-4" />
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default BookingPage;
